import { useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { apiError } from "@/lib/api";
import { useEnterpriseContact } from "@/hooks/usePayments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Users, BookMarked, Palette, ShieldCheck, CheckCircle2 } from "lucide-react";

const FEATURES = [
  { icon: Users, title: "Organization management", desc: "Invite and manage employee accounts from one dashboard." },
  { icon: BookMarked, title: "Curated catalog", desc: "Publish specific courses to your team's learning paths." },
  { icon: Palette, title: "White-label branding", desc: "Your logo and colors across dashboards and certificates." },
  { icon: ShieldCheck, title: "Compliance tracking", desc: "Completion records and automated PDF certifications." },
];

export default function Enterprise() {
  const [form, setForm] = useState({ name: "", email: "", company: "", team_size: "", message: "" });
  const [done, setDone] = useState(false);

  const contact = useEnterpriseContact({
    onSuccess: () => { setDone(true); toast.success("Thanks! Our team will reach out shortly."); },
    onError: (e) => toast.error(apiError(e)),
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    contact.mutate(form);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-6 md:px-8 py-16 grid lg:grid-cols-2 gap-16">
        <div>
          <div className="inline-flex items-center gap-2 border border-border bg-card px-3 py-1 mb-6 text-xs font-semibold uppercase tracking-[0.2em]">
            <Building2 className="h-3.5 w-3.5 text-primary" /> Learn Me for Enterprise
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">
            Upskill your <span className="text-primary">entire team.</span>
          </h1>
          <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-md">
            Give every employee on-demand, AI-generated training — with the controls, branding and
            compliance reporting your organization needs.
          </p>
          <div className="mt-10 space-y-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex gap-4">
                <div className="h-10 w-10 bg-secondary flex items-center justify-center shrink-0"><f.icon className="h-5 w-5 text-primary" /></div>
                <div>
                  <h3 className="font-display font-bold">{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border bg-card p-8 h-fit">
          {done ? (
            <div className="text-center py-12" data-testid="enterprise-success">
              <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-6" />
              <h2 className="font-display text-2xl font-bold">Request received!</h2>
              <p className="text-muted-foreground mt-2">Our team will be in touch within one business day.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="enterprise-form">
              <h2 className="font-display text-2xl font-bold mb-1">Talk to sales</h2>
              <p className="text-sm text-muted-foreground mb-6">Tell us about your team and we'll build a plan.</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider mb-2 block">Full name</Label>
                  <Input required value={form.name} onChange={update("name")} className="rounded-none h-11" data-testid="enterprise-name-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider mb-2 block">Work email</Label>
                  <Input type="email" required value={form.email} onChange={update("email")} className="rounded-none h-11" data-testid="enterprise-email-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider mb-2 block">Company</Label>
                  <Input required value={form.company} onChange={update("company")} className="rounded-none h-11" data-testid="enterprise-company-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider mb-2 block">Team size</Label>
                  <Select value={form.team_size} onValueChange={(v) => setForm((f) => ({ ...f, team_size: v }))}>
                    <SelectTrigger className="rounded-none h-11" data-testid="enterprise-teamsize-trigger"><SelectValue placeholder="Select size" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1–10</SelectItem>
                      <SelectItem value="11-50">11–50</SelectItem>
                      <SelectItem value="51-200">51–200</SelectItem>
                      <SelectItem value="200+">200+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider mb-2 block">What do you need?</Label>
                  <Textarea value={form.message} onChange={update("message")} className="rounded-none min-h-24" data-testid="enterprise-message-input" />
                </div>
              </div>
              <Button type="submit" disabled={contact.isPending} className="w-full mt-6 rounded-none h-11 bg-primary hover:opacity-90" data-testid="enterprise-submit-button">
                {contact.isPending ? "Sending..." : "Request a demo"}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
